/**
 * OnboardingEditorModalV2 Component
 *
 * Full-screen onboarding canvas editor for V2 StepNode flows.
 * that reads/writes from the screensV2 subcollection.
 *
 * Key differences from V1:
 * - Route mode only (no modal/portal mode needed)
 * - Uses useOnboardingEditorActionsV2 (StepNode-based export/import/deploy)
 * - Uses OnboardingCanvasEditorV2 (V2-native canvas, no bridge)
 *
 * Sprint: Schema V2 Migration - Sprint 2
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import {
  OnboardingCanvasEditorV2,
  type OnboardingCanvasEditorV2Handle,
} from '@modules/reactFlow/components/onboarding/OnboardingCanvasEditorV2';
import { extractStrategyIdFromStrategyBlocksMetadata } from '@modules/strategy-blocks/utils/strategyScreenTransplant';
import { useOnboardingEditorActionsV2 } from '../hooks/useOnboardingEditorActionsV2';
import { OnboardingEditorActionsV2 } from './OnboardingEditorActionsV2';
import type { Onboarding, OnboardingVersion } from '@modules/firebase-core/converters/onboardingConverter';
import type { StepNode } from '@segmently/schema-models/v2';
import { useGuideHighlight } from '@modules/guides/hooks/useGuideHighlight';

export interface OnboardingEditorModalV2Props {
  projectId: string;
  onboardingId: string;
  versionId?: string; // Optional - will load latest if not provided
}

export const OnboardingEditorModalV2: React.FC<OnboardingEditorModalV2Props> = ({
  projectId,
  onboardingId,
  versionId: initialVersionId,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  // DC-12: the canvas anchor `deploy-web` appears once `customActions` renders;
  // the bounded 5s lookup (25 x 200ms) covers the Firestore load.
  useGuideHighlight();

  // Services
  const onboardingService = useMemo(() => new OnboardingService(), []);
  // Data loading state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [initialVersion, setInitialVersion] = useState<OnboardingVersion | null>(null);
  // I4 (AC-020): an unknown ?version= falls back to the latest version with an
  // honest dismissible notice — never a crash, never a silent substitution.
  const [versionFallbackNotice, setVersionFallbackNotice] = useState<string | null>(null);
  const [associatedStrategyOverride, setAssociatedStrategyOverride] = useState<string | null | undefined>(undefined);
  const canvasControlRef = useRef<OnboardingCanvasEditorV2Handle | null>(null);

  // Note: Language settings are passed to canvas editor via version props directly

  // Load onboarding and version data for the V2 editor.
  useEffect(() => {
    async function loadData() {
      if (!projectId || !onboardingId) {
        setError('Missing project or onboarding ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Load onboarding
        const loadedOnboarding = await onboardingService.getOnboarding(projectId, onboardingId);
        if (!loadedOnboarding) {
          setError('Onboarding not found');
          setLoading(false);
          return;
        }
        setOnboarding(loadedOnboarding);

        // Load version
        let version: OnboardingVersion | null = null;
        if (initialVersionId) {
          version = await onboardingService.getVersion(projectId, onboardingId, initialVersionId);
          if (!version) {
            version = await onboardingService.getLatestVersion(projectId, onboardingId);
            if (version) {
              setVersionFallbackNotice(
                `Version "${initialVersionId}" was not found — showing the latest version instead.`,
              );
            }
          }
        } else {
          version = await onboardingService.getLatestVersion(projectId, onboardingId);
        }

        if (!version) {
          setError('No versions found for this onboarding');
          setLoading(false);
          return;
        }

        setInitialVersion(version);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load onboarding';
        setError(message);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [projectId, onboardingId, initialVersionId, onboardingService]);

  // Actions hook (V2 — uses StepNodeScreenService)
  const actions = useOnboardingEditorActionsV2({
    projectId,
    onboardingId,
    initialVersionId: initialVersion?.id || '',
    userId: currentUser?.uid || '',
  });

  // Close handler — navigate to V2 onboardings list
  const handleClose = useCallback(() => {
    navigate(`/project/${projectId}/onboardings-v2`);
  }, [navigate, projectId]);

  // Navigate back (for error state)
  const handleBack = useCallback(() => {
    navigate(`/project/${projectId}/onboardings-v2`);
  }, [navigate, projectId]);

  const versionForAssociation = actions.selectedVersion || initialVersion;
  const metadataAssociatedStrategyId = useMemo(
    () => extractStrategyIdFromStrategyBlocksMetadata(versionForAssociation?.screensGraph?.metadata) ?? null,
    [versionForAssociation],
  );

  useEffect(() => {
    setAssociatedStrategyOverride(undefined);
  }, [actions.selectedVersionId, initialVersion?.id]);

  const associatedStrategyId = associatedStrategyOverride !== undefined
    ? associatedStrategyOverride
    : metadataAssociatedStrategyId;

  // Freeze guard (Spec A / D51, A4-3/4): a published version is read-only; the
  // banner CTA mints a new (unfrozen) version and switches the editor to it.
  const isFrozen = Boolean(actions.selectedVersion?.frozen);
  const handleCreateEditableVersion = useCallback(async () => {
    await actions.createVersion(`v${actions.versions.length + 1}`);
  }, [actions]);
  // R7/P3 (no-dead-end): a screen-editor SAVE on the frozen version mints a new
  // version WITH the pending payload — the user's edit is never lost to the freeze.
  const handleSaveToNewVersion = useCallback(async (step: unknown) => {
    await actions.createVersion(`v${actions.versions.length + 1}`, {
      carryScreenPatch: step as StepNode,
    });
  }, [actions]);

  // U13 (Spec D2, AC-D2-8): head = newest version; selecting any other node
  // views it read-only (frozen keeps its own banner and wins).
  const headVersionId = useMemo(() => {
    if (actions.versions.length === 0) return undefined;
    return [...actions.versions]
      .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))[0]?.id;
  }, [actions.versions]);
  const isViewingNonHead = Boolean(
    headVersionId
    && actions.selectedVersionId
    && actions.selectedVersionId !== headVersionId
    && !isFrozen,
  );
  const handleBackToLatest = useCallback(() => {
    if (headVersionId) actions.switchVersion(headVersionId);
  }, [actions, headVersionId]);

  const routeScreenId = searchParams.get('screenId') || undefined;

  const setRouteScreenId = useCallback((screenId: string | null) => {
    const currentScreenId = searchParams.get('screenId');
    if ((screenId || null) === currentScreenId) return;

    const nextParams = new URLSearchParams(searchParams);
    if (screenId) {
      nextParams.set('screenId', screenId);
    } else {
      nextParams.delete('screenId');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Custom actions for TopControlPanel
  const customActions = useMemo(() => {
    if (!initialVersion || !onboarding) return null;
    return (
      <OnboardingEditorActionsV2
        actions={actions}
        projectId={projectId}
        onboardingId={onboardingId}
        onboardingName={onboarding.name}
        associatedStrategyId={associatedStrategyId}
        onAssociateStrategy={() => canvasControlRef.current?.openStrategyAssociationDialog()}
        onOpenAssociatedStrategy={() => canvasControlRef.current?.openAssociatedStrategy()}
        onChangeTheme={() => canvasControlRef.current?.openChangeTheme()}
      />
    );
  }, [actions, associatedStrategyId, initialVersion, onboarding, projectId, onboardingId]);

  // Loading state
  if (loading) {
    return (
      <Box
        data-testid="editor-modal-v2-container"
        sx={{
          height: '100vh',
          width: '100vw',
          position: 'relative',
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        data-testid="editor-modal-v2-container"
        sx={{
          height: '100vh',
          width: '100vw',
          position: 'relative',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Missing data
  if (!onboarding || !initialVersion) {
    return (
      <Box
        data-testid="editor-modal-v2-container"
        sx={{
          height: '100vh',
          width: '100vw',
          position: 'relative',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">Missing required data</Alert>
      </Box>
    );
  }

  // Main content — route mode only (no portal needed)
  return (
    <Box
      data-testid="editor-modal-v2-container"
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'relative',
        bgcolor: 'background.default',
      }}
    >
      <OnboardingCanvasEditorV2
        key={actions.selectedVersionId}
        open={true}
        onClose={handleClose}
        projectId={projectId}
        onboardingId={onboardingId}
        versionId={actions.selectedVersionId || initialVersion.id}
        onboardingName={onboarding.name}
        versionName={actions.selectedVersion?.name || initialVersion.name}
        embedded={true}
        hideTopPanel={false}
        customActions={customActions}
        controlRef={canvasControlRef}
        onStrategyAssociationChanged={setAssociatedStrategyOverride}
        associatedStrategyId={associatedStrategyId}
        autoOpenScreenId={routeScreenId}
        onScreenEditorOpen={(screenId) => setRouteScreenId(screenId)}
        onScreenEditorClose={() => setRouteScreenId(null)}
        defaultLanguage={
          actions.selectedVersion?.screensGraph?.defaultLanguage ||
          initialVersion.screensGraph?.defaultLanguage
        }
        languages={
          actions.selectedVersion?.screensGraph?.languages ||
          initialVersion.screensGraph?.languages
        }
        platform={onboarding.platform}
        frozen={isFrozen}
        onCreateEditableVersion={handleCreateEditableVersion}
        onSaveToNewVersion={handleSaveToNewVersion}
        viewingOnly={isViewingNonHead}
        viewingOnlyVersionName={actions.selectedVersion?.name}
        onBackToLatest={handleBackToLatest}
        onSwitchVersion={actions.switchVersion}
      />
      {versionFallbackNotice && (
        <Alert
          severity="warning"
          data-testid="editor-modal-v2-version-fallback"
          onClose={() => setVersionFallbackNotice(null)}
          sx={{
            position: 'absolute',
            top: 72,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: theme => theme.zIndex.snackbar,
            boxShadow: 3,
          }}
        >
          {versionFallbackNotice}
        </Alert>
      )}
    </Box>
  );
};

export default OnboardingEditorModalV2;
